import FileSaver from 'file-saver';
import i18next from 'i18next';
import { createTypes, completeTypes, withPostSuccess, withPostFailure } from 'redux-recompose';

import RejectedOrderService from '~services/RejectedOrders/service';
import Snackbar from '~app/components/@core/Snackbar';
import ModalActions from '~redux/Modal/actions';
import { ORDER_STATUS } from '~constants/orders';
import OrderService from '~services/Order/service';

import { mapFilters } from './mappers';
import { checkOrderWithFilters } from './checkOrderWithFilters';
import { REJECTED_ORDERS_TAB_NAMES } from './constant';

export const actions = createTypes(
  completeTypes([
    'SET_ACTIVE_TAB',
    'GET_REJECTED_ORDERS',
    'ADD_NEW_ORDER',
    'ASSIGN_ORDER',
    'NEW_ASSIGNED_ORDER',
    'SET_FILTERS',
    'SET_SELECTED_ORDER',
    'INJECT_ORDER',
    'REMOVE_INJECTED_ORDER',
    'DOWNLOAD_REJECTED_ORDERS',
    'GET_NEW_ORDER_REJECTED_ORDER'
  ]),
  '@@REJECTED_ORDER_MANAGER'
);

export const targets = {
  ACTIVE_TAB: 'activeTab',
  REJECTED_ORDERS: 'rejectedOrders',
  ABORT_CONTROLLER: 'abortController',
  NEW_ORDERS: 'newOrders',
  ASSINGNED_ORDER: 'assignedOrder',
  NEW_ASSIGNED_ORDERS: 'newAssignedOrders',
  FILTERS: 'filters',
  SELECTED_ORDER: 'selectedOrder',
  INJECTED_ORDER: 'injectedOrder',
  REJECTED_ORDERS_DOWNLOADING: 'rejectedOrdersDownloading',
  REJECTED_ORDERS_STACK: 'rejectedOrdersStack'
};

const actionCreators = {
  setActiveTab: ({ name }) => ({
    type: actions.SET_ACTIVE_TAB,
    target: targets.ACTIVE_TAB,
    payload: name
  }),
  getRejectedOrders: ({ status, filters, page, cancelToken, orderStatus }) => ({
    type: actions.GET_REJECTED_ORDERS,
    target: targets.REJECTED_ORDERS,
    payload: {
      status,
      filters: filters ? mapFilters(filters, orderStatus) : '',
      page,
      cancelToken
    },
    service: RejectedOrderService.getOrders
  }),
  addNewOrder: ({ orderId, storeId }) => (dispatch, getState) => {
    const state = getState();
    const { activeTab } = state.order || {};

    if (activeTab?.toLowerCase() !== ORDER_STATUS.REJECTED_ORDER_MANAGER) {
      return;
    }

    return dispatch({
      type: actions.GET_NEW_ORDER_REJECTED_ORDER,
      target: targets.REJECTED_ORDERS_STACK,
      payload: {
        id: orderId,
        storeId
      },
      service: OrderService.getOrderById,
      injections: [
        withPostSuccess((dispatch, { data: order }, state) => {
          const { filters } = state.rejectedOrderManager || {};
          const { activeTab: activeRejectedOrderTab } = state.rejectedOrderManager || {};

          const isValid = checkOrderWithFilters(order, filters);
          Snackbar.warning(
            `${i18next.t('Home:newPosRejectedOrder', {
              orderId: order.id
            })}`
          );

          if (isValid && activeRejectedOrderTab === REJECTED_ORDERS_TAB_NAMES.rejectedOrders) {
            return dispatch({
              type: actions.ADD_NEW_ORDER,
              target: targets.NEW_ORDERS,
              payload: order
            });
          }
        })
      ]
    });
  },
  assignOrder: ({ orderId, successMessage, errorMessage }) => ({
    type: actions.ASSIGN_ORDER,
    target: targets.ASSINGNED_ORDER,
    payload: { id: orderId },
    service: RejectedOrderService.assignOrder,
    injections: [
      withPostSuccess(() => {
        Snackbar.success(successMessage);
      }),
      withPostFailure(() => {
        Snackbar.error(errorMessage);
      })
    ]
  }),
  handleNewAssignedOrder: ({ order }) => (dispatch, getState) => {
    const state = getState();
    const { activeTab } = state.order || {};
    const { activeTab: activeRejectedOrderTab } = state.rejectedOrderManager || {};

    if (
      activeTab?.toLowerCase() !== ORDER_STATUS.REJECTED_ORDER_MANAGER ||
      activeRejectedOrderTab === REJECTED_ORDERS_TAB_NAMES.rejectedOrders
    ) {
      return;
    }

    return dispatch({
      type: actions.NEW_ASSIGNED_ORDER,
      target: targets.NEW_ASSIGNED_ORDERS,
      payload: order
    });
  },
  setFilters: ({ filters }) => ({
    type: actions.SET_FILTERS,
    target: targets.FILTERS,
    payload: filters
  }),
  injectOrder: ({ orderId, fbdId, successMessage, errorMessage, onSuccess }) => ({
    type: actions.INJECT_ORDER,
    target: targets.INJECTED_ORDER,
    payload: { id: orderId, fbdId },
    service: RejectedOrderService.injectOrder,
    injections: [
      withPostSuccess((dispatch) => {
        Snackbar.success(successMessage);
        dispatch(actionCreators.setSelectedOrder(null));
        dispatch(ModalActions.clearModals());
        if (onSuccess) {
          onSuccess();
        }
      }),
      withPostFailure(() => {
        Snackbar.error(errorMessage);
      })
    ]
  }),
  setSelectedOrder: (order) => ({
    type: actions.SET_SELECTED_ORDER,
    target: targets.SELECTED_ORDER,
    payload: order
  }),
  updateRejectedOrder: ({ order }) => (dispatch, getState) => {
    const state = getState();
    const { activeTab } = state.order || {};
    const { activeTab: activeRejectedOrderTab } = state.rejectedOrderManager || {};

    if (
      activeTab?.toLowerCase() !== ORDER_STATUS.REJECTED_ORDER_MANAGER ||
      activeRejectedOrderTab === REJECTED_ORDERS_TAB_NAMES.rejectedOrders
    ) {
      return;
    }

    return dispatch({
      type: actions.REMOVE_INJECTED_ORDER,
      target: targets.REJECTED_ORDERS,
      payload: order
    });
  },
  downloadRejectedOrders: (filters, setIsDownloading) => (dispatch) => {
    return dispatch({
      type: actions.DOWNLOAD_REJECTED_ORDERS,
      target: targets.REJECTED_ORDERS_DOWNLOADING,
      payload: { filters: filters ? mapFilters(filters) : '' },
      service: RejectedOrderService.downloadRejectedOrders,
      injections: [
        withPostSuccess((_, response) => {
          setIsDownloading(false);
          const csv = new Blob([response.data], {
            type: 'application/csv'
          });
          FileSaver.saveAs(csv, 'rejected_orders.csv');
          Snackbar.success(i18next.t('RejectedOrderManager:downloadedReport'));
        }),
        withPostFailure(() => Snackbar.error(i18next.t('RejectedOrderManager:orderError')))
      ]
    });
  }
};

export default actionCreators;
